import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  makeStyles,
  Tooltip,
} from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import LampIcon from "src/components/LampIcon";
import { type Theme } from "src/theme";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CloseIcon from "@material-ui/icons/Close";
import LoadableButton from "src/components/LoadableButton";
import { StatefulLoadableIconButton } from "src/components/LoadableIconButton";
import LinearProgress from "@material-ui/core/LinearProgress";
import { toast } from "react-toastify";

interface SetupCardProps {
  title: string;
  imageSrc: string;
  description: string;
  buttonText: string;
  onButtonClick: () => void;
  points: number;
  disabled?: boolean;
  tooltipText?: string;
  completed?: boolean;
  isRunning?: boolean;
  footerNote?: string;
  onManualComplete?: () => Promise<void>;
  /** Shown as a chip when the platform already has an integration. */
  connectedNote?: string;
}

const useStyles = makeStyles((theme: Theme) => {
  const { custom: palette, derived } = theme.palette.custom;

  return {
    root: {
      width: 268,
      margin: "auto",
      height: 375,
      display: "flex",
      flexDirection: "column",
      borderStyle: "solid",
      borderColor: palette.brandSecondary,
      borderWidth: theme.spacing(1),
      borderRadius: theme.shape.borderRadius,
      boxShadow: derived.shadow.sm,
    },
    media: {
      height: 150,
      padding: theme.spacing(1, 1, 0, 1),
      margin: theme.spacing(0, 1),
      width: `calc(100% - ${theme.spacing(2)}px)`,
      objectFit: "contain",
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: "#ffffff",
      borderRadius: theme.shape.borderRadius,
    },
    titleContent: {
      padding: theme.spacing(0.5, 1),
      "&:last-child": {
        paddingBottom: 0,
      },
    },
    cardTitle: {
      fontSize: theme.typography.h6.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
    },
    content: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      paddingBottom: theme.spacing(4.5),
    },
    contentWithFooter: {
      paddingBottom: theme.spacing(1),
    },
    buttonArea: {
      marginTop: "auto",
      justifyContent: "center",
      display: "flex",
    },
    mrgIcon: {
      backgroundColor:
        theme.palette.custom.appearance === "custom"
          ? theme.palette.custom.customDarkBg
          : palette.brandPrimary,
      color: derived.onPrimary,
      width: theme.spacing(3),
      height: theme.spacing(3),
      "&:hover": {
        backgroundColor: derived.brandPrimaryLight,
      },
    },
    checkIcon: {
      color: derived.secondaryOnBg,
      alignSelf: "center",
      transform: "scale(1.8)",
    },
    lampButton: {
      marginRight: theme.spacing(1),
    },
    lampIcon: {
      width: theme.spacing(3),
      height: theme.spacing(3),
      margin: theme.spacing(-1),
    },
    manualCompleteWrapper: {
      display: "flex",
      marginLeft: "auto",
    },
    manualCompleteButton: {
      color: theme.palette.text.secondary,
    },
    connectedChip: {
      alignSelf: "flex-start",
      marginBottom: theme.spacing(0.5),
      color: derived.secondaryOnBg,
      borderColor: derived.secondaryOnBg,
    },
    connectedChipIcon: {
      color: derived.secondaryOnBg,
    },
    loadingProgress: {
      borderRadius: 25,
      height: 10,
    },
    footerNote: {
      padding: theme.spacing(0.5, 1),
    },
  };
});

const SetupCard: React.FC<SetupCardProps> = ({
  title,
  imageSrc,
  description,
  buttonText,
  onButtonClick,
  points,
  disabled = false,
  tooltipText,
  completed = false,
  isRunning = false,
  footerNote,
  onManualComplete,
  connectedNote,
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);

  return (
    <Card className={classes.root}>
      <CardContent className={classes.titleContent}>
        <Box display="flex" alignItems="center">
          <IconButton className={`${classes.mrgIcon} ${classes.lampButton}`}>
            <LampIcon
              variant={completed ? "gold" : "white"}
              className={classes.lampIcon}
            />
          </IconButton>
          <Typography component="h2" className={classes.cardTitle}>
            {title}
          </Typography>
          {onManualComplete && !completed ? (
            <Tooltip title="Click here to manually complete the card.">
              <span className={classes.manualCompleteWrapper}>
                <StatefulLoadableIconButton
                  size="small"
                  aria-label={`Manually complete ${title}`}
                  className={classes.manualCompleteButton}
                  // Caught here rather than left to the button's own handler so
                  // a Firestore permission error doesn't reach the user raw.
                  onClick={async () => {
                    try {
                      await onManualComplete();
                    } catch {
                      toast.error(
                        "We couldn't complete this card. Please try again."
                      );
                    }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </StatefulLoadableIconButton>
              </span>
            </Tooltip>
          ) : null}
        </Box>
      </CardContent>
      <CardMedia
        className={classes.media}
        image={imageSrc}
        title={title}
        component="img"
      />
      <CardContent
        className={
          footerNote
            ? `${classes.content} ${classes.contentWithFooter}`
            : classes.content
        }
      >
        {connectedNote && !completed ? (
          <Chip
            size="small"
            variant="outlined"
            icon={<CheckCircleIcon className={classes.connectedChipIcon} />}
            label={connectedNote}
            className={classes.connectedChip}
          />
        ) : null}
        <Box display="flex" minHeight="50px" justifyContent="space-between">
          <Typography
            variant="body2"
            color="textSecondary"
            component="p"
            style={{ flex: 1 }}
          >
            {description}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            component="p"
            style={{ marginLeft: 8, whiteSpace: "nowrap" }}
          >
            {points}
          </Typography>
        </Box>
        <div className={classes.buttonArea}>
          {completed ? (
            <>
              {isRunning ? (
                <Tooltip title={"Importing your data now!"}>
                  <span>
                    <LinearProgress className={classes.loadingProgress} />
                  </span>
                </Tooltip>
              ) : (
                <CheckCircleIcon className={classes.checkIcon} />
              )}
            </>
          ) : (
            <>
              {tooltipText ? (
                <Tooltip title={tooltipText}>
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={onButtonClick}
                      disabled={disabled}
                      fullWidth
                    >
                      {buttonText}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <>
                  {loading ? (
                    <LoadableButton
                      color="primary"
                      variant="contained"
                      type="submit"
                      loading={true}
                      disabled={true}
                    >
                      Loading Integration
                    </LoadableButton>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={async () => {
                        setLoading(true);
                        try {
                          await onButtonClick();
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={disabled}
                      fullWidth
                    >
                      {buttonText}
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </CardContent>
      {footerNote ? (
        <Typography
          variant="caption"
          color="textSecondary"
          display="block"
          className={classes.footerNote}
        >
          {footerNote}
        </Typography>
      ) : null}
    </Card>
  );
};

export default SetupCard;
